import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, Stack, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import UpsellPolicyPicker, { type UpsellPolicy, type UpsellCatalogItem } from '../../../src/components/upsells/UpsellPolicyPicker';
import Input from '../../../src/components/Input';
import { useToast } from '../../../src/components/Toast';
import InlineConfirm from '../../../src/components/InlineConfirm';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { formatDateTime } from '../../../src/utils/timezone';
import { titleCase } from '../../../src/utils/format';
import { AdminToolbar } from '../../../src/components/AdminList';
import VehicleProfileModal, { VehicleFields } from '../../../src/components/VehicleProfileModal';

interface Location {
  id: string;
  name: string;
}

interface Plan {
  upsellPolicy?: Record<string, 'none' | 'purchasable' | 'included'>;
  includedUpsellIds?: string[];
  id: string;
  name: string;
  description: string | null;
  planType?: 'automatic' | 'self_serve' | 'mixed';
  tierName: string | null;
  priceMonthly: number;
  maxWashesPerDay: number | null;
  maxWashesPerMonth: number | null;
  maxMinutesPerDay: number | null;
  maxMinutesPerMonth: number | null;
  maxVacMinutesPerDay?: number | null;
  maxVacMinutesPerMonth?: number | null;
  vendingCreditCentsPerMonth?: number | null;
  // Membership sessions ignore the bay's per-session minimum when true.
  overrideMinimums?: boolean;
  locationScope: string;
  locationIds: string[] | null;
  locationNames: string[];
  isActive: boolean;
  subscriberCount: number;
}

interface Subscriber {
  id: string;
  userId: string;
  userName: string;
  email: string | null;
  phone: string | null;
  accountNumber: string;
  status: string;
  suspended: boolean;
  suspendedReason: string | null;
  stripeSubscriptionId: string | null;
  washesUsedToday: number;
  washesUsedThisMonth: number;
  minutesUsedToday?: number;
  minutesUsedThisMonth?: number;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  vehicle: {
    licensePlate: string;
    make: string;
    model: string;
    color: string;
    year: number | null;
    description: string;
  } | null;
}

/** PlanDetailScreen - view/edit subscription plan and manage subscribers. */
export default function PlanDetailScreen({ entityId }: { entityId?: string } = {}) {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const params = useLocalSearchParams<{ id: string }>();
  const id = entityId ?? params.id;
  const router = useRouter();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [subsTotal, setSubsTotal] = useState(0);
  const [subsPage, setSubsPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [refundConfirmSubId, setRefundConfirmSubId] = useState<string | null>(null);
  // Owner override edit of a subscriber's registered vehicle (bypasses the
  // customer 15-day lock; re-locks immediately).
  const [vehicleEditSub, setVehicleEditSub] = useState<Subscriber | null>(null);
  // Suspend / unsuspend / end actions (mig 0056). One confirm open at a time.
  type SubAction = 'suspend' | 'unsuspend' | 'end_period' | 'end_now';
  const [actionConfirm, setActionConfirm] = useState<{ subId: string; action: SubAction } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const performSubAction = async (sub: Subscriber, action: SubAction) => {
    setActionBusy(true);
    try {
      if (action === 'suspend') {
        await scope.api.post(`/api/owner/subscriptions/${sub.id}/suspend`, {});
        toast.show({ type: 'success', title: 'Membership suspended', message: 'Redemptions blocked and billing paused.' });
      } else if (action === 'unsuspend') {
        await scope.api.post(`/api/owner/subscriptions/${sub.id}/unsuspend`, {});
        toast.show({ type: 'success', title: 'Suspension lifted', message: 'Redemptions and billing resume.' });
      } else {
        await scope.api.post(`/api/owner/subscriptions/${sub.id}/end`, { when: action === 'end_now' ? 'now' : 'period_end' });
        toast.show({
          type: 'success',
          title: action === 'end_now' ? 'Membership ended' : 'Cancellation scheduled',
          message: action === 'end_now' ? 'The membership is canceled immediately (no refund).' : 'Access continues until the end of the paid period.',
        });
      }
      setActionConfirm(null);
      fetchSubscribers(subsPage);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setActionBusy(false);
    }
  };

  const ACTION_CONFIRM_COPY: Record<SubAction, { msg: (name: string) => string; label: string; variant: 'warning' | 'danger' }> = {
    suspend: { msg: n => `Suspend ${n}'s membership? They can't redeem washes and Stripe billing pauses until you unsuspend.`, label: 'Suspend', variant: 'warning' },
    unsuspend: { msg: n => `Lift the suspension on ${n}'s membership? Redemptions and billing resume immediately.`, label: 'Unsuspend', variant: 'warning' },
    end_period: { msg: n => `End ${n}'s membership at the end of the paid period? No refund; access continues until then.`, label: 'End at Period End', variant: 'warning' },
    end_now: { msg: n => `End ${n}'s membership right now with no refund? This cannot be undone.`, label: 'End Now', variant: 'danger' },
  };
  const toast = useToast();
  const scope = useOwnerScope();

  // Editable fields
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDailyLimit, setEditDailyLimit] = useState('');
  const [editMonthlyLimit, setEditMonthlyLimit] = useState('');
  const [editMinutesDaily, setEditMinutesDaily] = useState('');
  const [editMinutesMonthly, setEditMinutesMonthly] = useState('');
  // Optional vacuum-minute benefit — toggle off sends null for both caps.
  const [editIncludeVac, setEditIncludeVac] = useState(false);
  const [editVacDaily, setEditVacDaily] = useState('');
  const [editVacMonthly, setEditVacMonthly] = useState('');
  const [editOverrideMinimums, setEditOverrideMinimums] = useState(false);
  // Optional vending-credit benefit — dollars/month, no unlimited mode.
  const [editIncludeVending, setEditIncludeVending] = useState(false);
  const [editVendingMonthly, setEditVendingMonthly] = useState('');
  const [editUpsellPolicy, setEditUpsellPolicy] = useState<UpsellPolicy>({}); // 1.4 R05
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  const [editLocationScope, setEditLocationScope] = useState<'selected' | 'all'>('all');
  const [editLocationIds, setEditLocationIds] = useState<string[]>([]);
  const [locationsList, setLocationsList] = useState<Location[]>([]);

  const fetchSubscribers = async (p = subsPage, searchOverride?: string) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      const s = searchOverride ?? searchQuery;
      if (s) params.set('search', s);
      const data = await scope.api.get<{ subscribers: Subscriber[]; total: number }>(`/api/subscription-plans/${id}/subscribers?${params}`);
      setSubscribers(data.subscribers);
      setSubsTotal(data.total);
      setSubsPage(p);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plans, locs] = await Promise.all([
        scope.api.get<Plan[]>('/api/subscription-plans'),
        scope.api.get<Location[]>('/api/locations'),
        fetchSubscribers(1),
      ]);
      setLocationsList(locs);
      const thisPlan = plans.find(p => p.id === id);
      if (thisPlan) {
        setPlan(thisPlan);
        setEditName(thisPlan.name);
        setEditDescription(thisPlan.description || '');
        setEditDailyLimit(thisPlan.maxWashesPerDay != null ? String(thisPlan.maxWashesPerDay) : '');
        setEditMonthlyLimit(thisPlan.maxWashesPerMonth != null ? String(thisPlan.maxWashesPerMonth) : '');
        setEditMinutesDaily(thisPlan.maxMinutesPerDay != null ? String(thisPlan.maxMinutesPerDay) : '');
        setEditMinutesMonthly(thisPlan.maxMinutesPerMonth != null ? String(thisPlan.maxMinutesPerMonth) : '');
        setEditIncludeVac(thisPlan.maxVacMinutesPerDay != null || thisPlan.maxVacMinutesPerMonth != null);
        setEditVacDaily(thisPlan.maxVacMinutesPerDay != null ? String(thisPlan.maxVacMinutesPerDay) : '');
        setEditVacMonthly(thisPlan.maxVacMinutesPerMonth != null ? String(thisPlan.maxVacMinutesPerMonth) : '');
        setEditIncludeVending(thisPlan.vendingCreditCentsPerMonth != null);
        setEditVendingMonthly(thisPlan.vendingCreditCentsPerMonth != null ? (thisPlan.vendingCreditCentsPerMonth / 100).toFixed(2) : '');
        setEditOverrideMinimums(!!thisPlan.overrideMinimums);
        setEditLocationScope(thisPlan.locationScope as 'selected' | 'all');
        setEditLocationIds(thisPlan.locationIds || []);
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchData(); }, []));

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const startEditing = () => {
    if (!plan) return;
    setEditName(plan.name);
    setEditDescription(plan.description || '');
    setEditDailyLimit(plan.maxWashesPerDay != null ? String(plan.maxWashesPerDay) : '');
    setEditMonthlyLimit(plan.maxWashesPerMonth != null ? String(plan.maxWashesPerMonth) : '');
    setEditMinutesDaily(plan.maxMinutesPerDay != null ? String(plan.maxMinutesPerDay) : '');
    setEditMinutesMonthly(plan.maxMinutesPerMonth != null ? String(plan.maxMinutesPerMonth) : '');
    setEditIncludeVac(plan.maxVacMinutesPerDay != null || plan.maxVacMinutesPerMonth != null);
    setEditVacDaily(plan.maxVacMinutesPerDay != null ? String(plan.maxVacMinutesPerDay) : '');
    setEditVacMonthly(plan.maxVacMinutesPerMonth != null ? String(plan.maxVacMinutesPerMonth) : '');
    setEditIncludeVending(plan.vendingCreditCentsPerMonth != null);
    setEditVendingMonthly(plan.vendingCreditCentsPerMonth != null ? (plan.vendingCreditCentsPerMonth / 100).toFixed(2) : '');
    setEditOverrideMinimums(!!plan.overrideMinimums);
    setEditLocationScope(plan.locationScope as 'selected' | 'all');
    setEditLocationIds(plan.locationIds || []);
    setEditUpsellPolicy({ ...(plan.upsellPolicy ?? {}), ...Object.fromEntries((plan.includedUpsellIds ?? []).map(id => [id, 'included' as const])) });
    scope.api.get<UpsellCatalogItem[]>('/api/upsells').then(list => setUpsellCatalog(list.filter(u => u.isActive !== false))).catch(() => setUpsellCatalog([]));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
  };

  const saveChanges = async () => {
    if (!plan) return;
    if (!editName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Plan name is required' });
      return;
    }
    const planType = plan.planType ?? 'automatic';
    const includesAuto = planType === 'automatic' || planType === 'mixed';
    const includesSelf = planType === 'self_serve' || planType === 'mixed';
    const daily = includesAuto ? parseInt(editDailyLimit) : null;
    const monthly = includesAuto ? parseInt(editMonthlyLimit) : null;
    const minDay = includesSelf ? parseInt(editMinutesDaily) : null;
    const minMonth = includesSelf ? parseInt(editMinutesMonthly) : null;
    // 0 is valid: it means unlimited.
    if (includesAuto && (daily == null || isNaN(daily) || daily < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a daily wash limit (0 = unlimited)' });
      return;
    }
    if (includesAuto && (monthly == null || isNaN(monthly) || monthly < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a monthly wash limit (0 = unlimited)' });
      return;
    }
    if (includesSelf && (minDay == null || isNaN(minDay) || minDay < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a daily minute limit (0 = unlimited)' });
      return;
    }
    if (includesSelf && (minMonth == null || isNaN(minMonth) || minMonth < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a monthly minute limit (0 = unlimited)' });
      return;
    }
    if (includesAuto && daily! > 0 && monthly! > 0 && daily! > monthly!) {
      toast.show({ type: 'error', title: 'Error', message: 'Daily wash limit cannot exceed the monthly limit' });
      return;
    }
    if (includesSelf && minDay! > 0 && minMonth! > 0 && minDay! > minMonth!) {
      toast.show({ type: 'error', title: 'Error', message: 'Daily minute limit cannot exceed the monthly limit' });
      return;
    }
    const vacDay = editIncludeVac ? parseInt(editVacDaily) : null;
    const vacMonth = editIncludeVac ? parseInt(editVacMonthly) : null;
    if (editIncludeVac && (vacDay == null || isNaN(vacDay) || vacDay < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a daily vacuum minute limit (0 = unlimited)' });
      return;
    }
    if (editIncludeVac && (vacMonth == null || isNaN(vacMonth) || vacMonth < 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a monthly vacuum minute limit (0 = unlimited)' });
      return;
    }
    if (editIncludeVac && vacDay! > 0 && vacMonth! > 0 && vacDay! > vacMonth!) {
      toast.show({ type: 'error', title: 'Error', message: 'Daily vacuum minute limit cannot exceed the monthly limit' });
      return;
    }
    const vendingCents = editIncludeVending ? Math.round(parseFloat(editVendingMonthly) * 100) : null;
    if (editIncludeVending && (!vendingCents || vendingCents <= 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid monthly vending credit amount' });
      return;
    }

    if (editLocationScope === 'selected' && editLocationIds.length === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one location' });
      return;
    }

    setSaving(true);
    try {
      await scope.api.api(`/api/subscription-plans/${id}`, {
        method: 'PUT',
        body: {
          name: editName.trim(),
          description: editDescription.trim() || null,
          maxWashesPerDay: daily,
          maxWashesPerMonth: monthly,
          maxMinutesPerDay: minDay,
          maxMinutesPerMonth: minMonth,
          maxVacMinutesPerDay: vacDay,
          maxVacMinutesPerMonth: vacMonth,
          vendingCreditCentsPerMonth: vendingCents,
          overrideMinimums: editOverrideMinimums,
          locationScope: editLocationScope,
          locationIds: editLocationScope === 'all' ? null : editLocationIds,
          upsellPolicy: editUpsellPolicy,
        },
      });
      const locNames = editLocationScope === 'selected'
        ? editLocationIds.map(lid => locationsList.find(l => l.id === lid)?.name).filter(Boolean) as string[]
        : [];
      setPlan({
        ...plan,
        name: editName.trim(),
        description: editDescription.trim() || null,
        maxWashesPerDay: daily,
        maxWashesPerMonth: monthly,
        maxMinutesPerDay: minDay,
        maxMinutesPerMonth: minMonth,
        maxVacMinutesPerDay: vacDay,
        maxVacMinutesPerMonth: vacMonth,
        vendingCreditCentsPerMonth: vendingCents,
        overrideMinimums: editOverrideMinimums,
        locationScope: editLocationScope,
        locationIds: editLocationScope === 'all' ? null : editLocationIds,
        locationNames: locNames,
        upsellPolicy: editUpsellPolicy,
        includedUpsellIds: Object.entries(editUpsellPolicy).filter(([, v]) => v === 'included').map(([k]) => k),
      });
      setEditing(false);
      toast.show({ type: 'success', title: 'Plan Updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await scope.api.api(`/api/subscription-plans/${id}`, {
        method: 'PUT',
        body: { isActive: !plan.isActive },
      });
      setPlan({ ...plan, isActive: !plan.isActive });
      toast.show({ type: 'success', title: plan.isActive ? 'Plan Deactivated' : 'Plan Activated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await scope.api.api(`/api/subscription-plans/${id}`, { method: 'DELETE' });
      toast.show({ type: 'success', title: 'Plan Deleted' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getDeleteMessage = () => {
    const activeCount = plan?.subscriberCount ?? 0;
    return activeCount > 0
      ? `This plan has ${activeCount} active subscriber${activeCount > 1 ? 's' : ''}. Deleting will deactivate it so no new subscribers can join, but existing subscriptions will continue until canceled. Delete this plan?`
      : 'Delete this plan? This cannot be undone.';
  };

  const handleRefund = async (sub: Subscriber) => {
    setRefunding(sub.id);
    try {
      await scope.api.post(`/api/subscriptions/${sub.id}/refund`, {});
      toast.show({ type: 'success', title: 'Refunded', message: `${titleCase(sub.userName)}'s subscription has been canceled and refunded.` });
      setRefundConfirmSubId(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Refund Failed', message: err.message });
    } finally {
      setRefunding(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'past_due': return colors.warning;
      case 'canceled': return colors.danger;
      default: return colors.textSecondary;
    }
  };

  if (loading || !plan) {
    return (
      <>
        <Stack.Screen options={{ title: 'Plan Details' }} />
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: plan.name }} />
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}
      >
        <ContentContainer>
        {/* Plan Info — view or edit mode */}
        {editing ? (
          <Card style={styles.infoCard}>
            <Text style={styles.editSectionTitle}>Edit Plan</Text>
            {/* Tier is deliberately read-only: current members' entitlements
                reference it, so changing it would silently change what every
                existing subscriber gets. Create a new plan for a new tier. */}
            {plan.tierName && (
              <View style={{ marginBottom: spacing.md }}>
                <Text style={styles.infoLabel}>Wash Tier</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: 2 }}>
                  <Ionicons name="lock-closed" size={14} color={colors.textLight} />
                  <Text style={styles.infoValue}>
                    {/^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName}
                  </Text>
                </View>
                <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 2 }}>
                  Locked — current members' washes are tied to this tier. Create a new plan to offer a different tier.
                </Text>
              </View>
            )}
            <Input label="Plan Name" value={editName} onChangeText={setEditName} />
            <Input label="Description" value={editDescription} onChangeText={setEditDescription} placeholder="Optional" multiline maxLength={500} />
            {((plan.planType ?? 'automatic') !== 'self_serve') && (
              <>
                <Input label="Max Washes Per Day (0 = unlimited)" value={editDailyLimit} onChangeText={setEditDailyLimit} keyboardType="number-pad" />
                <Input label="Max Washes Per Month (0 = unlimited)" value={editMonthlyLimit} onChangeText={setEditMonthlyLimit} keyboardType="number-pad" />
              </>
            )}
            {(plan.planType === 'self_serve' || plan.planType === 'mixed') && (
              <>
                <Input label="Max Minutes Per Day (self-serve, 0 = unlimited)" value={editMinutesDaily} onChangeText={setEditMinutesDaily} keyboardType="number-pad" />
                <Input label="Max Minutes Per Month (self-serve, 0 = unlimited)" value={editMinutesMonthly} onChangeText={setEditMinutesMonthly} keyboardType="number-pad" />
              </>
            )}
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm }}
              onPress={() => setEditIncludeVac(!editIncludeVac)}
            >
              <Ionicons name={editIncludeVac ? 'checkbox' : 'square-outline'} size={22} color={editIncludeVac ? colors.primary : colors.textLight} />
              <Text style={{ fontSize: fontSize.sm, color: colors.text }}>Include vacuum minutes</Text>
            </TouchableOpacity>
            {editIncludeVac && (
              <>
                <Input label="Max Vacuum Minutes Per Day (0 = unlimited)" value={editVacDaily} onChangeText={(v: string) => setEditVacDaily(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />
                <Input label="Max Vacuum Minutes Per Month (0 = unlimited)" value={editVacMonthly} onChangeText={(v: string) => setEditVacMonthly(v.replace(/[^0-9]/g, ''))} keyboardType="number-pad" />
              </>
            )}
            {/* Shown for minute-based benefits (self-serve or vacuum) — those
                sessions carry the bay's per-session minimum unless overridden. */}
            {(plan.planType === 'self_serve' || plan.planType === 'mixed' || editIncludeVac) && (
              <>
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm }}
                  onPress={() => setEditOverrideMinimums(!editOverrideMinimums)}
                >
                  <Ionicons name={editOverrideMinimums ? 'checkbox' : 'square-outline'} size={22} color={editOverrideMinimums ? colors.primary : colors.textLight} />
                  <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.text }}>
                    Override minimums — membership sessions ignore the bay minimum
                  </Text>
                </TouchableOpacity>
                <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginBottom: spacing.xs }}>
                  Off: stopping a membership session early still deducts the bay's minimum minutes.
                </Text>
              </>
            )}
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm }}
              onPress={() => setEditIncludeVending(!editIncludeVending)}
            >
              <Ionicons name={editIncludeVending ? 'checkbox' : 'square-outline'} size={22} color={editIncludeVending ? colors.primary : colors.textLight} />
              <Text style={{ fontSize: fontSize.sm, color: colors.text }}>Include vending credit</Text>
            </TouchableOpacity>
            {editIncludeVending && (
              <Input label="Vending Credit Per Month ($)" value={editVendingMonthly} onChangeText={(v: string) => setEditVendingMonthly(v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" placeholder="5.00" />
            )}
            <UpsellPolicyPicker catalog={upsellCatalog} value={editUpsellPolicy} onChange={setEditUpsellPolicy} title="Add-ons for members" />
            <Text style={[styles.infoLabel, { marginTop: spacing.md, marginBottom: spacing.xs }]}>Location Scope</Text>
            <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
              {(['all', 'selected'] as const).map(scope => (
                <TouchableOpacity
                  key={scope}
                  onPress={() => { setEditLocationScope(scope); if (scope === 'all') setEditLocationIds([]); }}
                  style={[styles.scopeChip, editLocationScope === scope && { backgroundColor: colors.primary, borderColor: colors.primary }]}
                >
                  <Text style={[styles.scopeChipText, editLocationScope === scope && { color: '#fff' }]}>
                    {scope === 'all' ? 'All Locations' : 'Selected'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {editLocationScope === 'selected' && (
              <View style={{ gap: spacing.xs }}>
                {locationsList.map(loc => (
                  <TouchableOpacity
                    key={loc.id}
                    onPress={() => setEditLocationIds(prev => prev.includes(loc.id) ? prev.filter(x => x !== loc.id) : [...prev, loc.id])}
                    style={[styles.locOption, editLocationIds.includes(loc.id) && { borderColor: colors.primary, backgroundColor: colors.primary + '10' }]}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                      <Ionicons name={editLocationIds.includes(loc.id) ? 'checkbox' : 'square-outline'} size={20} color={editLocationIds.includes(loc.id) ? colors.primary : colors.textLight} />
                      <Text style={[styles.locOptionText, editLocationIds.includes(loc.id) && { color: colors.primary, fontWeight: fontWeight.semibold }]}>{loc.name}</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
            <View style={styles.editButtons}>
              <Button title="Cancel" onPress={cancelEditing} variant="outline" style={{ flex: 1 }} />
              <Button title="Save" onPress={saveChanges} loading={saving} style={{ flex: 1 }} />
            </View>
          </Card>
        ) : (
          <Card style={styles.infoCard}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Tier</Text>
              <Text style={styles.infoValue}>{plan.tierName ? (/^[1-4]$/.test(plan.tierName) ? `Option ${plan.tierName}` : plan.tierName) : '—'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Price</Text>
              <Text style={styles.infoValue}>{fmt(plan.priceMonthly)}/month</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Daily Limit</Text>
              <Text style={styles.infoValue}>{plan.maxWashesPerDay} wash{plan.maxWashesPerDay !== 1 ? 'es' : ''}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Monthly Limit</Text>
              <Text style={styles.infoValue}>{plan.maxWashesPerMonth} washes</Text>
            </View>
            {(plan.maxVacMinutesPerDay != null || plan.maxVacMinutesPerMonth != null) && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Vacuum Minutes</Text>
                <Text style={styles.infoValue}>
                  {plan.maxVacMinutesPerDay === 0 ? 'Unlimited/day' : `${plan.maxVacMinutesPerDay ?? '—'}/day`} · {plan.maxVacMinutesPerMonth === 0 ? 'Unlimited/mo' : `${plan.maxVacMinutesPerMonth ?? '—'}/mo`}
                </Text>
              </View>
            )}
            {plan.vendingCreditCentsPerMonth != null && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Vending Credit</Text>
                <Text style={styles.infoValue}>${(plan.vendingCreditCentsPerMonth / 100).toFixed(2)}/mo</Text>
              </View>
            )}
            {plan.overrideMinimums && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Session Minimums</Text>
                <Text style={styles.infoValue}>Overridden — bay minimum ignored</Text>
              </View>
            )}
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Scope</Text>
              <Text style={styles.infoValue}>
                {plan.locationScope === 'all' ? 'All locations' : (plan.locationNames.length > 0 ? plan.locationNames.join(', ') : 'Selected locations')}
              </Text>
            </View>
            {plan.description && (
              <View style={[styles.infoRow, { borderBottomWidth: 0 }]}>
                <Text style={styles.infoLabel}>Description</Text>
                <Text style={[styles.infoValue, { flex: 1, textAlign: 'right' }]}>{plan.description}</Text>
              </View>
            )}
            <Button
              title="Edit Plan"
              onPress={startEditing}
              variant="outline"
              size="sm"
              icon={<Ionicons name="pencil" size={16} color={colors.primary} />}
              style={{ marginTop: spacing.md }}
            />
          </Card>
        )}

        {/* Action buttons */}
        <View style={styles.actionRow}>
          <Button
            title={plan.isActive ? 'Deactivate' : 'Activate'}
            onPress={toggleActive}
            loading={saving}
            variant={plan.isActive ? 'outline' : 'primary'}
            style={{ flex: 1 }}
          />
          <Button
            title="Delete"
            onPress={() => setShowDeleteConfirm(true)}
            loading={deleting}
            variant="outline"
            style={{ flex: 1, borderColor: colors.danger }}
            textStyle={{ color: colors.danger }}
            icon={<Ionicons name="trash" size={16} color={colors.danger} />}
          />
        </View>
        <InlineConfirm
          message={getDeleteMessage()}
          visible={showDeleteConfirm}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmLabel="Delete Plan"
          loading={deleting}
          variant="danger"
        />

        {/* Subscribers */}
        <View style={styles.subsHeaderRow}>
          <Text style={styles.sectionTitle}>Subscribers</Text>
        </View>

        <AdminToolbar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={() => fetchSubscribers(1)}
          total={subsTotal}
          page={subsPage}
          limit={10}
          onPageChange={(p) => fetchSubscribers(p)}
          csvEndpoint={`/api/subscription-plans/${id}/subscribers${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`}
          csvFilename={`${(plan.name || 'plan').replace(/[^a-zA-Z0-9]/g, '_')}_subscribers.csv`}
          label="subscriber"
        />

        {subscribers.length === 0 && (
          <Card style={{ alignItems: 'center', padding: spacing.lg }}>
            <Text style={{ color: colors.textLight }}>{searchQuery ? 'No subscribers found' : 'No subscribers yet'}</Text>
          </Card>
        )}

        {subscribers.map(sub => (
          <Card key={sub.id} style={styles.subCard}>
            <View style={styles.subHeader}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Text style={styles.subName}>{titleCase(sub.userName)}</Text>
                  <Text style={styles.acctBadge}>#{sub.accountNumber}</Text>
                </View>
              </View>
              {sub.suspended && (
                <View style={[styles.statusBadge, { backgroundColor: colors.danger + '20', marginRight: spacing.xs }]}>
                  <Text style={[styles.statusText, { color: colors.danger }]}>suspended</Text>
                </View>
              )}
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(sub.status) + '20' }]}>
                <Text style={[styles.statusText, { color: getStatusColor(sub.status) }]}>{sub.status}</Text>
              </View>
            </View>
            {sub.suspended && sub.suspendedReason ? (
              <Text style={{ fontSize: fontSize.xs, color: colors.danger, marginBottom: spacing.xs }}>
                Reason: {sub.suspendedReason}
              </Text>
            ) : null}
            {/* Registered vehicle — tap to override-edit (support cases; ignores the customer 15-day lock) */}
            <TouchableOpacity style={styles.vehicleRow} onPress={() => setVehicleEditSub(sub)} accessibilityRole="button">
              <Ionicons name="car-outline" size={14} color={sub.vehicle ? colors.primary : colors.textLight} />
              <Text style={[styles.subStat, { flex: 1 }, !sub.vehicle && { fontStyle: 'italic' }]}>
                {sub.vehicle ? `${sub.vehicle.description} — ${sub.vehicle.licensePlate}` : 'No vehicle registered'}
              </Text>
              <Ionicons name="create-outline" size={14} color={colors.primary} />
            </TouchableOpacity>
            <View style={styles.subStats}>
              {(plan.planType ?? 'automatic') !== 'self_serve' && (
                <>
                  <Text style={styles.subStat}>Washes today: {sub.washesUsedToday}/{plan.maxWashesPerDay ?? '—'}</Text>
                  <Text style={styles.subStat}>Washes month: {sub.washesUsedThisMonth}/{plan.maxWashesPerMonth ?? '—'}</Text>
                </>
              )}
              {(plan.planType === 'self_serve' || plan.planType === 'mixed') && (
                <>
                  <Text style={styles.subStat}>Minutes today: {(sub.minutesUsedToday ?? 0).toFixed(1)}/{plan.maxMinutesPerDay ?? '—'}</Text>
                  <Text style={styles.subStat}>Minutes month: {(sub.minutesUsedThisMonth ?? 0).toFixed(1)}/{plan.maxMinutesPerMonth ?? '—'}</Text>
                </>
              )}
              {sub.currentPeriodEnd && (
                <Text style={styles.subStat}>Renews: {formatDateTime(sub.currentPeriodEnd)}</Text>
              )}
              {sub.cancelAtPeriodEnd && (
                <Text style={[styles.subStat, { color: colors.warning }]}>Cancels at period end</Text>
              )}
            </View>
            {sub.status !== 'canceled' && (
              <>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm }}>
                  <Button
                    title={sub.suspended ? 'Unsuspend' : 'Suspend'}
                    onPress={() => setActionConfirm({ subId: sub.id, action: sub.suspended ? 'unsuspend' : 'suspend' })}
                    variant="outline"
                    size="sm"
                    style={{ borderColor: colors.warning }}
                    textStyle={{ color: colors.warning }}
                    icon={<Ionicons name={sub.suspended ? 'play-circle-outline' : 'pause-circle-outline'} size={14} color={colors.warning} />}
                  />
                  {!sub.cancelAtPeriodEnd && (
                    <Button
                      title="End at Period End"
                      onPress={() => setActionConfirm({ subId: sub.id, action: 'end_period' })}
                      variant="outline"
                      size="sm"
                      icon={<Ionicons name="time-outline" size={14} color={colors.primary} />}
                    />
                  )}
                  <Button
                    title="End Now"
                    onPress={() => setActionConfirm({ subId: sub.id, action: 'end_now' })}
                    variant="outline"
                    size="sm"
                    style={{ borderColor: colors.danger }}
                    textStyle={{ color: colors.danger }}
                    icon={<Ionicons name="close-circle-outline" size={14} color={colors.danger} />}
                  />
                  <Button
                    title={refunding === sub.id ? 'Refunding...' : 'Refund & Cancel'}
                    onPress={() => setRefundConfirmSubId(sub.id)}
                    loading={refunding === sub.id}
                    variant="outline"
                    size="sm"
                    style={{ borderColor: colors.danger }}
                    textStyle={{ color: colors.danger }}
                    icon={<Ionicons name="card-outline" size={14} color={colors.danger} />}
                  />
                </View>
                {actionConfirm?.subId === sub.id && (
                  <InlineConfirm
                    message={ACTION_CONFIRM_COPY[actionConfirm.action].msg(titleCase(sub.userName))}
                    visible={true}
                    onConfirm={() => performSubAction(sub, actionConfirm.action)}
                    onCancel={() => setActionConfirm(null)}
                    confirmLabel={ACTION_CONFIRM_COPY[actionConfirm.action].label}
                    loading={actionBusy}
                    variant={ACTION_CONFIRM_COPY[actionConfirm.action].variant}
                  />
                )}
                <InlineConfirm
                  message={`Cancel ${titleCase(sub.userName)}'s subscription and refund their latest payment? This cannot be undone.`}
                  visible={refundConfirmSubId === sub.id}
                  onConfirm={() => handleRefund(sub)}
                  onCancel={() => setRefundConfirmSubId(null)}
                  confirmLabel="Refund & Cancel"
                  loading={refunding === sub.id}
                  variant="warning"
                />
              </>
            )}
          </Card>
        ))}

        <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>

      <VehicleProfileModal
        visible={vehicleEditSub !== null}
        mode="owner"
        initial={vehicleEditSub?.vehicle ?? null}
        contextLine={vehicleEditSub ? `${titleCase(vehicleEditSub.userName)} — #${vehicleEditSub.accountNumber}` : undefined}
        onSubmit={async (fields: VehicleFields) => {
          if (!vehicleEditSub) return;
          await scope.api.put(`/api/owner/subscriptions/${vehicleEditSub.id}/vehicle`, fields);
          setVehicleEditSub(null);
          toast.show({ type: 'success', title: 'Vehicle updated', message: 'The registered vehicle has been saved.' });
          fetchSubscribers(subsPage);
        }}
        onClose={() => setVehicleEditSub(null)}
      />
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary },

  infoCard: { marginBottom: spacing.md },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  infoLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  infoValue: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },

  editSectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  editButtons: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },
  scopeChip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  scopeChipText: { fontSize: fontSize.sm, color: c.text },
  locOption: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  locOptionText: { fontSize: fontSize.sm, color: c.text },

  actionRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },

  subsHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  exportBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.md, backgroundColor: c.primary + '10' },
  exportText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary },

  searchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, marginBottom: spacing.md },
  searchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, paddingVertical: spacing.sm },

  subCard: { marginBottom: spacing.sm },
  subHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs },
  subName: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  acctBadge: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: c.primary, backgroundColor: c.primary + '10', paddingHorizontal: 6, paddingVertical: 1, borderRadius: borderRadius.sm, overflow: 'hidden' },
  subEmail: { fontSize: fontSize.xs, color: c.textLight },
  statusBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full },
  statusText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium },
  subStats: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  subStat: { fontSize: fontSize.xs, color: c.textSecondary },
  vehicleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, marginBottom: spacing.xs },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
